import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Avatar
} from '@mui/material';
import { Login, Logout, AccessTime, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { base_url, getToken } from '../../config/config';

const CheckInCheckOut = () => {
    const { user } = useSelector((state) => state.user);
    const [todayStatus, setTodayStatus] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Redirect if not logged in
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        fetchTodayStatus();
    }, [user, navigate]);

    const fetchTodayStatus = async () => {
        if (!user) return;
        
        try {
            const token = getToken();
            const response = await axios.get(`${base_url}/v1/management-log/today-status`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setTodayStatus(response.data.data || {});
        } catch (error) {
            console.error('Error fetching today status:', error);
            if (error.response?.status === 401) {
                navigate('/login');
            }
            setTodayStatus({});
        }
    };

    const getEmployeeName = (employee) => {
        if (typeof employee === 'string') return employee;
        return employee.name || employee.fullName || employee.username || '';
    };

    const handleCheckIn = async () => {
        if (!user) return;

        setLoading(true);
        setError('');

        try {
            const token = getToken();
            const response = await axios.post(`${base_url}/v1/management-log/checkin`, {
                location: 'office',
                notes: 'Check-in via legacy interface'
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.status === 1) {
                setTodayStatus(response.data.data);
                localStorage.setItem('isCheckedIn', 'true');
                localStorage.setItem('checkInTime', response.data.data.checkInTime);
                
                // Navigate to TaskLogger if it exists
                navigate('/TaskLogger', { 
                    state: { 
                        employeeName: user.name || user.email, 
                        checkInTime: response.data.data.checkInTime 
                    } 
                });
            } else {
                setError(response.data.message || 'Check-in fehlgeschlagen');
            }
        } catch (error) {
            console.error('Check-in error:', error);
            if (error.response?.status === 401) {
                navigate('/login');
            } else {
                setError(error.response?.data?.message || 'Check-in fehlgeschlagen');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCheckOut = async () => {
        if (!user) return;

        setLoading(true);
        setError('');

        try {
            const token = getToken();
            const response = await axios.post(`${base_url}/v1/management-log/checkout`, {
                notes: 'Check-out via legacy interface'
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.status === 1) {
                setTodayStatus(response.data.data);
                localStorage.setItem('isCheckedIn', 'false');
                localStorage.removeItem('checkInTime');
            } else {
                setError(response.data.message || 'Check-out fehlgeschlagen');
            }
        } catch (error) {
            console.error('Check-out error:', error);
            if (error.response?.status === 401) {
                navigate('/login');
            } else {
                setError(error.response?.data?.message || 'Check-out fehlgeschlagen');
            }
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'working': return 'info';
            case 'completed': return 'success';
            case 'present': return 'primary';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'working': return 'Arbeitet';
            case 'completed': return 'Abgeschlossen';
            case 'present': return 'Anwesend';
            case 'absent': return 'Abwesend';
            default: return 'Unbekannt';
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return timeString;
    };

    const canCheckIn = !todayStatus.checkInTime || todayStatus.workingStatus === 'absent';
    const canCheckOut = todayStatus.checkInTime && !todayStatus.checkOutTime;

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 4 }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        mb: 3
                    }}
                >
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
                        <AccessTime sx={{ mr: 1, fontSize: 40 }} />
                        Check In/Check Out
                    </Typography>
                    <Typography variant="subtitle1" align="center" sx={{ opacity: 0.9 }}>
                        Erweiterte Anwesenheitsverwaltung
                    </Typography>
                </Paper>

                <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            {(user?.name || user?.email || '').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="h6">
                                {user?.name || user?.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Angemeldeter Benutzer
                            </Typography>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {user && todayStatus && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Heutiger Status:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                <Chip
                                    icon={<CheckCircle />}
                                    label={`Status: ${getStatusLabel(todayStatus.workingStatus)}`}
                                    color={getStatusColor(todayStatus.workingStatus)}
                                    size="small"
                                />
                                {todayStatus.checkInTime && (
                                    <Chip
                                        icon={<Login />}
                                        label={`Check-in: ${formatTime(todayStatus.checkInTime)}`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {todayStatus.checkOutTime && (
                                    <Chip
                                        icon={<Logout />}
                                        label={`Check-out: ${formatTime(todayStatus.checkOutTime)}`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {todayStatus.totalWorkingHours > 0 && (
                                    <Chip
                                        icon={<AccessTime />}
                                        label={`Arbeitszeit: ${Math.floor(todayStatus.totalWorkingHours)}h ${Math.round((todayStatus.totalWorkingHours % 1) * 60)}m`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        {canCheckIn && (
                            <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                onClick={handleCheckIn}
                                disabled={loading || !user}
                                startIcon={loading ? <CircularProgress size={20} /> : <Login />}
                                sx={{ p: 1.5, fontSize: '16px', fontWeight: 'bold' }}
                            >
                                {loading ? 'Check-in...' : 'Check In'}
                            </Button>
                        )}

                        {canCheckOut && (
                            <Button
                                variant="contained"
                                color="error"
                                fullWidth
                                onClick={handleCheckOut}
                                disabled={loading || !user}
                                startIcon={loading ? <CircularProgress size={20} /> : <Logout />}
                                sx={{ p: 1.5, fontSize: '16px', fontWeight: 'bold' }}
                            >
                                {loading ? 'Check-out...' : 'Check Out'}
                            </Button>
                        )}

                        {!canCheckIn && !canCheckOut && todayStatus.checkOutTime && (
                            <Box sx={{ textAlign: 'center', width: '100%' }}>
                                <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                                    ✅ Arbeitstag abgeschlossen
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Check-out um {formatTime(todayStatus.checkOutTime)}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/advanced-management-log')}
                            sx={{ mr: 1 }}
                        >
                            Erweiterte Ansicht
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => navigate('/TaskLogger', { state: { employeeName: user?.name || user?.email } })}
                        >
                            Task Logger
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default CheckInCheckOut;